/**
 * Mock data for documentary screening (Nex-Com methodology).
 * Phase 1 = first pass; phase 2 delta = coverage + snowballing addendum.
 */

export const PIPELINE_STEPS = [
  {
    key: "institutional",
    labelFr: "Sites institutionnels nationaux",
    labelEn: "National institutional websites",
  },
  {
    key: "pubmed",
    labelFr: "PubMed / PMC / Europe PMC",
    labelEn: "PubMed / PMC / Europe PMC",
  },
  {
    key: "cochrane",
    labelFr: "Cochrane Library",
    labelEn: "Cochrane Library",
  },
  {
    key: "societies",
    labelFr: "Sociétés savantes & guidelines",
    labelEn: "Learned societies & guidelines",
  },
  {
    key: "scholar",
    labelFr: "Google Scholar & open access",
    labelEn: "Google Scholar & open access",
  },
  {
    key: "tri",
    labelFr: "Tri, inclusion, matrice de couverture",
    labelEn: "Screening, inclusion, coverage matrix",
  },
]

export const PHASE1_AXES_MARKDOWN = `| Axe | Intitulé | Question | Mots-clés FR | Mots-clés EN |
|-----|----------|----------|--------------|--------------|
| 1 | Épidémiologie nationale | Incidence/mortalité récentes en France ? | cardiovasculaire femme France incidence | cardiovascular disease women France epidemiology |
| 2 | Facteurs de risque spécifiques | Quels FDR chez la femme ? | facteur risque cardiovasculaire femme | cardiovascular risk factors women |
| 3 | Guidelines | Recommandations ESC/AHA/HAS récentes ? | recommandation HAS cardiovasculaire | guideline cardiovascular women |`

export const PHASE1_SEARCH_LOG = [
  {
    id: "l1",
    base: "Santé publique France / BEH",
    query: '"maladies cardiovasculaires" femme site:santepubliquefrance.fr',
    raw: 42,
    kept: 5,
  },
  {
    id: "l2",
    base: "HAS",
    query: '"cardiovasculaire" recommandation HAS',
    raw: 28,
    kept: 3,
  },
  {
    id: "l3",
    base: "PubMed",
    query: "(cardiovascular diseases[MeSH]) AND (women) AND (epidemiology)",
    raw: 612,
    kept: 12,
  },
  {
    id: "l4",
    base: "Cochrane",
    query: "cardiovascular women systematic review",
    raw: 19,
    kept: 4,
  },
]

export const PHASE1_COVERAGE = [
  {
    axisId: "1",
    axisLabel: "Épidémiologie nationale",
    downloadable: 5,
    inaccessible: 1,
    total: 6,
    status: "OK",
  },
  {
    axisId: "2",
    axisLabel: "Facteurs de risque spécifiques",
    downloadable: 1,
    inaccessible: 0,
    total: 1,
    status: "FAIBLE",
  },
  {
    axisId: "3",
    axisLabel: "Guidelines",
    downloadable: 4,
    inaccessible: 2,
    total: 6,
    status: "OK",
  },
]

export const PHASE1_INDEX_MARKDOWN = `# Index du corpus documentaire (aperçu — passe 1)
## Maladies cardiovasculaires chez la femme (mock)

**Date de recherche** : 2026-04-12
**Méthode** : Recherche documentaire structurée (simulation)
**Sources téléchargeables** : 10
**Sources inaccessibles** : 3
**Total identifié** : 13

---

## A. SOURCES TÉLÉCHARGEABLES (extrait)

### AXE 1 : Épidémiologie nationale
| # | Référence | Année | Type | URL/DOI |
|---|-----------|-------|------|---------|
| 1 | SPF. Points épidémiologie CVD femmes. 2024. | 2024 | Rapport | https://www.santepubliquefrance.fr/ (mock) |

### AXE 2 : Facteurs de risque
| # | Référence | Année | Type | URL/DOI |
|---|-----------|-------|------|---------|
| 2 | Revue narrative — hormones et risque. | 2022 | Revue | https://doi.org/10.0000/mock.2 |

> **Note passe 1** : l’axe 2 est sous-documenté (< 2 sources). Une passe ciblée + snowballing est recommandée.
`

export const PHASE1_INACCESSIBLE = [
  {
    id: "p1",
    ref: "Lancet Commission on Women and CVD. Lancet. 2024.",
    journal: "Lancet",
    reason: "Paywall",
    coveredBy: "—",
    lacune: "Non",
  },
  {
    id: "p2",
    ref: "ESC Guidelines 2023 — section femmes.",
    journal: "Eur Heart J",
    reason: "Paywall",
    coveredBy: "HAS synthèse (mock)",
    lacune: "Non",
  },
]

export const PHASE2_EXTRA_LOG = [
  {
    id: "l5",
    base: "PubMed (passe 2)",
    query: '("cardiovascular risk factors" AND women AND menopause) NOT review[pt]',
    raw: 88,
    kept: 6,
  },
  {
    id: "l6",
    base: "Snowballing",
    query: "Backward + forward sur refs. 01, 03, 07",
    raw: 34,
    kept: 5,
  },
]

export const PHASE2_COVERAGE = [
  {
    axisId: "1",
    axisLabel: "Épidémiologie nationale",
    downloadable: 6,
    inaccessible: 1,
    total: 7,
    status: "OK",
  },
  {
    axisId: "2",
    axisLabel: "Facteurs de risque spécifiques",
    downloadable: 4,
    inaccessible: 0,
    total: 4,
    status: "OK",
  },
  {
    axisId: "3",
    axisLabel: "Guidelines",
    downloadable: 5,
    inaccessible: 2,
    total: 7,
    status: "OK",
  },
]

export const PHASE2_INDEX_APPEND = `

---

## Mise à jour — passe 2 (couverture & snowballing)

**Sources téléchargeables** : +6 (total mock : 16)
**Sources inaccessibles** : +1 (total mock : 4)

### AXE 2 : Facteurs de risque (complété)
| # | Référence | Année | Type | URL/DOI |
|---|-----------|-------|------|---------|
| 11 | Cohorte nationale — Ménopause et risque CV. | 2023 | Cohorte | https://doi.org/10.0000/mock.11 |
| 12 | Méta-analyse FDR sexo-spécifiques. | 2024 | MA | https://doi.org/10.0000/mock.12 |

Snowballing : 5 références récurrentes intégrées depuis les bibliographies des sources pivots (mock).
`

export const PHASE2_EXTRA_INACCESSIBLE = [
  {
    id: "p3",
    ref: "Circulation. Éditorial récent sur disparités de sexe.",
    journal: "Circulation",
    reason: "Paywall",
    coveredBy: "Revue open access ref. 12",
    lacune: "Non",
  },
]

export const PHASE1_WEAK_AXIS_MESSAGE_FR =
  "L’axe « Facteurs de risque spécifiques » est faible (< 2 sources téléchargeables). La méthodologie README recommande une passe 2 : recherches ciblées + snowballing sur les sources les plus citées."

export const PHASE1_WEAK_AXIS_MESSAGE_EN =
  "The “Sex-specific risk factors” axis is weak (< 2 downloadable sources). The README methodology recommends a second pass: targeted search + snowballing on the most-cited sources."

/** Full result after merging phase 1 + phase 2 (same as single-shot end state). */
export function buildMergedScreeningResult() {
  return {
    axesMarkdown: PHASE1_AXES_MARKDOWN,
    searchLog: [...PHASE1_SEARCH_LOG, ...PHASE2_EXTRA_LOG],
    coverage: PHASE2_COVERAGE,
    indexMarkdown: `${PHASE1_INDEX_MARKDOWN.trimEnd()}${PHASE2_INDEX_APPEND}`,
    inaccessible: [...PHASE1_INACCESSIBLE, ...PHASE2_EXTRA_INACCESSIBLE],
  }
}

export function buildPhase1ScreeningResult() {
  return {
    axesMarkdown: PHASE1_AXES_MARKDOWN,
    searchLog: [...PHASE1_SEARCH_LOG],
    coverage: [...PHASE1_COVERAGE],
    indexMarkdown: PHASE1_INDEX_MARKDOWN,
    inaccessible: [...PHASE1_INACCESSIBLE],
  }
}
